import React, { useContext, useState } from 'react';

/**
 * React UUID
 */
import uuid from 'react-uuid';

/**
 * Material UI
 */
import {
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    Grid,
    Icon,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

/**
 * Firebase
 */
import { firestore, getFirestoreTimestamp } from '../../../common/serviecs/firebase';

/**
 * Application
 */
import { AppContext } from '../../../App';
import { LoadingMask } from '../../../common/components/controls/LoadingMask';
import { UserSelectionMenu } from '../../../common/components/controls/UserSelection';
import {
    extractPlainTextFromRichText,
    plainTextOrMarkdownToRichTextValue,
    prepopulatedRichTextValue,
    RichTextEditor,
} from '../../../lib/slate/RichTextEditor';
import { callDeepSeekApi, DEEPSEEK_DEFAULT_MODEL, DEEPSEEK_MODELS } from '../../../common/functions/AIUtils';
import {
    buildMaterialGenerationPrompt,
    callDeepSeekWithRetries,
    derivePaperTitleAndSubtitle,
    generateChineseQuizFillInBlankPrompt,
    generateChineseQuizMultipleChoicePrompt,
    parseMaterialResponse,
    transformFillInBlankResponse,
    transformMultipleChoiceResponse,
} from './chineseQuizAiUtils';
import { normalizeLoadedPaper, sanitizeForFirestore } from './paperEditorUtils';

const DEFAULT_MATERIAL_LENGTH = 1200;
const BASELINE_MATERIAL_LENGTH = 1200;
const BASELINE_FILL_IN_BLANK_COUNT = 20;
const BASELINE_MULTIPLE_CHOICE_COUNT = 10;

const DIFFICULTY_LEVELS = {
    easy: { label: 'Easy', value: 1 },
    medium: { label: 'Medium', value: 3 },
    difficult: { label: 'Difficult', value: 5 },
};

const TARGET_LANGUAGES = {
    en: {
        label: 'English',
        promptCode: 'en',
        paperCode: 'en',
        subject: 'English',
        defaultTitle: 'Reading Quiz',
        fillInBlankTitle: 'Vocabulary Fill in the Blank',
        fillInBlankInstruction: 'Choose the correct word from the word bank to fill in each blank.',
        multipleChoiceTitle: 'Reading Comprehension',
        multipleChoiceInstruction: 'Choose the best answer based on the passage above.',
    },
    'zh-Hant': {
        label: '繁中',
        promptCode: 'zh',
        paperCode: 'zh',
        subject: 'Chinese',
        defaultTitle: 'Chinese Quiz',
        fillInBlankTitle: '詞彙填空',
        fillInBlankInstruction: '請從詞庫中選擇適當的詞語填入空格。',
        multipleChoiceTitle: '閱讀理解',
        multipleChoiceInstruction: '請根據上文內容選擇最適當的答案。',
    },
    'zh-Hans': {
        label: '簡中',
        promptCode: 'zh-Hans',
        paperCode: 'zh',
        subject: 'Chinese',
        defaultTitle: '中文测验',
        fillInBlankTitle: '词汇填空',
        fillInBlankInstruction: '请从词库中选择适当的词语填入空格。',
        multipleChoiceTitle: '阅读理解',
        multipleChoiceInstruction: '请根据上文内容选择最适当的答案。',
    },
};

function scaleQuestionCount(baselineCount, materialLength) {
    const length = Math.max(50, parseInt(materialLength, 10) || DEFAULT_MATERIAL_LENGTH);
    return Math.max(1, Math.round((baselineCount * length) / BASELINE_MATERIAL_LENGTH));
}

function defaultQuestionCounts(materialLength) {
    return {
        fillInBlank: scaleQuestionCount(BASELINE_FILL_IN_BLANK_COUNT, materialLength),
        multipleChoice: scaleQuestionCount(BASELINE_MULTIPLE_CHOICE_COUNT, materialLength),
    };
}

function withQuestionIds(questions) {
    return (questions || []).map((question) => Object.assign({}, question, { id: uuid() }));
}

function buildFillInBlankSection(transformed, materialRichText, targetLanguageKey) {
    const language = TARGET_LANGUAGES[targetLanguageKey] || TARGET_LANGUAGES['zh-Hant'];
    return {
        id: uuid(),
        type: 'fill-in-blank',
        title: transformed?.title || language.fillInBlankTitle,
        instruction: language.fillInBlankInstruction,
        richText: materialRichText,
        configurations: { provideOptions: true },
        questions: withQuestionIds(transformed?.questions || []),
    };
}

function buildMultipleChoiceSection(transformed, materialRichText, targetLanguageKey) {
    const language = TARGET_LANGUAGES[targetLanguageKey] || TARGET_LANGUAGES['zh-Hant'];
    return {
        id: uuid(),
        type: 'multiple-choice',
        title: transformed?.title || language.multipleChoiceTitle,
        instruction: language.multipleChoiceInstruction,
        richText: materialRichText,
        questions: withQuestionIds(transformed?.questions || []),
    };
}

export function ChineseQuizEditor(props) {
    const { account, user, goto } = props;
    const { setMessage } = useContext(AppContext);
    const classes = makeStyles((theme) => ({
        pageTitle: {
            fontWeight: 800,
            marginBottom: theme.spacing(1),
        },
        pageSubtitle: {
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(3),
        },
        sectionLabel: {
            fontWeight: 700,
            marginBottom: theme.spacing(1),
        },
        createForLabel: {
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
        },
        materialBox: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1),
            padding: theme.spacing(1),
            minHeight: 280,
            textAlign: 'left',
        },
        actionBar: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            marginTop: theme.spacing(2),
        },
        generateButton: {
            fontWeight: 800,
        },
        toggleGroup: {
            marginTop: theme.spacing(0.5),
        },
        toggleButton: {
            fontWeight: 700,
            textTransform: 'none',
            minWidth: 88,
        },
        aiPromptActions: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: theme.spacing(1),
        },
        sectionOptionRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(1),
        },
        questionCountField: {
            width: 120,
        },
        checkboxLabel: {
            fontWeight: 700,
        },
    }))();

    const initialQuestionCounts = defaultQuestionCounts(DEFAULT_MATERIAL_LENGTH);
    const [loading, setLoading] = useState([]);
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [materialLength, setMaterialLength] = useState(DEFAULT_MATERIAL_LENGTH);
    const [difficulty, setDifficulty] = useState('medium');
    const [targetLanguage, setTargetLanguage] = useState('zh-Hant');
    const [aiModel, setAiModel] = useState(DEEPSEEK_DEFAULT_MODEL);
    const [includeFillInBlank, setIncludeFillInBlank] = useState(true);
    const [includeMultipleChoice, setIncludeMultipleChoice] = useState(true);
    const [fillInBlankCount, setFillInBlankCount] = useState(initialQuestionCounts.fillInBlank);
    const [multipleChoiceCount, setMultipleChoiceCount] = useState(initialQuestionCounts.multipleChoice);
    const [materialRichText, setMaterialRichText] = useState(() => prepopulatedRichTextValue(''));
    const [materialMeta, setMaterialMeta] = useState({ title: null, subtitle: null });
    const [createForUser, setCreateForUser] = useState(null);
    const [paperMeta, setPaperMeta] = useState({
        school: '',
        grade: '',
        taggings: [],
    });

    const addLoading = (key) => {
        setLoading((current) => [...current, key]);
    };

    const removeLoading = (key) => {
        setLoading((current) => current.filter((item) => item !== key));
    };

    const populateCreateForAttributes = (selectedUser) => {
        setCreateForUser(selectedUser);
        setPaperMeta({
            school: selectedUser?.school || '',
            grade: selectedUser?.grade || '',
            taggings: selectedUser?.taggings || [],
        });
    };

    const updateMaterialLength = (value) => {
        const parsed = parseInt(value, 10);
        if (Number.isNaN(parsed)) {
            setMaterialLength('');
            return;
        }
        setMaterialLength(parsed);
        const counts = defaultQuestionCounts(parsed);
        setFillInBlankCount(counts.fillInBlank);
        setMultipleChoiceCount(counts.multipleChoice);
    };

    const parsePositiveInt = (value, fallback) => {
        const parsed = parseInt(value, 10);
        if (Number.isNaN(parsed) || parsed < 1) {
            return fallback;
        }
        return parsed;
    };

    const generateMaterial = async () => {
        if (!aiPrompt.trim()) {
            setMessage('Please describe the article you want AI to generate.');
            return;
        }

        const materialPrompt = buildMaterialGenerationPrompt(
            aiPrompt,
            paperMeta.grade,
            difficulty,
            targetLanguage,
            materialLength || DEFAULT_MATERIAL_LENGTH
        );
        addLoading('Generating reading material');
        const response = await callDeepSeekApi(user.idToken, '', materialPrompt, {
            jsonMode: true,
            model: aiModel,
        });
        removeLoading('Generating reading material');

        if (response?.success === false) {
            setMessage(response.message || 'Failed to generate reading material.');
            return;
        }

        const { title: materialTitle, subtitle: materialSubtitle, content } = parseMaterialResponse(response?.output?.text);
        if (!content) {
            setMessage('AI did not return any material. Please try a different prompt.');
            return;
        }

        setMaterialMeta({ title: materialTitle, subtitle: materialSubtitle });
        setMaterialRichText(plainTextOrMarkdownToRichTextValue(content));

        if (materialTitle && !title.trim()) {
            setTitle(materialTitle);
        }
        if (materialSubtitle && !subtitle.trim()) {
            setSubtitle(materialSubtitle);
        }
    };

    const generateQuiz = async () => {
        const materialText = extractPlainTextFromRichText(materialRichText);
        if (!materialText) {
            setMessage('Please paste reading material or generate it with AI first.');
            return;
        }

        if (!includeFillInBlank && !includeMultipleChoice) {
            setMessage('Please select at least one section to generate.');
            return;
        }

        const difficultyValue = DIFFICULTY_LEVELS[difficulty]?.value || DIFFICULTY_LEVELS.medium.value;
        const language = TARGET_LANGUAGES[targetLanguage] || TARGET_LANGUAGES['zh-Hant'];
        const promptLanguageCode = language.promptCode;
        const sections = [];
        let sectionTitle = null;
        let fillInBlankTransformed = null;
        let multipleChoiceTransformed = null;

        if (includeFillInBlank) {
            const questionCount = parsePositiveInt(fillInBlankCount, BASELINE_FILL_IN_BLANK_COUNT);
            const fillInBlankPrompt = generateChineseQuizFillInBlankPrompt(
                questionCount,
                difficultyValue,
                promptLanguageCode
            );
            addLoading('Generating vocabulary fill-in-blank questions');
            const fillInBlankResult = await callDeepSeekWithRetries(
                user.idToken,
                materialText,
                fillInBlankPrompt,
                transformFillInBlankResponse,
                3,
                { model: aiModel }
            );
            removeLoading('Generating vocabulary fill-in-blank questions');

            if (fillInBlankResult.error) {
                setMessage(fillInBlankResult.error || 'Failed to generate fill-in-blank questions.');
                return;
            }

            fillInBlankTransformed = fillInBlankResult.parsed;
            const fillInBlankSection = buildFillInBlankSection(fillInBlankTransformed, materialRichText, targetLanguage);
            if (!fillInBlankSection.questions.length) {
                setMessage('AI did not return enough fill-in-blank questions. Please try again.');
                return;
            }
            sections.push(fillInBlankSection);
            sectionTitle = fillInBlankTransformed?.title || sectionTitle;
        }

        if (includeMultipleChoice) {
            const questionCount = parsePositiveInt(multipleChoiceCount, BASELINE_MULTIPLE_CHOICE_COUNT);
            const multipleChoicePrompt = generateChineseQuizMultipleChoicePrompt(
                questionCount,
                difficultyValue,
                promptLanguageCode
            );
            addLoading('Generating reading comprehension questions');
            const multipleChoiceResult = await callDeepSeekWithRetries(
                user.idToken,
                materialText,
                multipleChoicePrompt,
                transformMultipleChoiceResponse,
                3,
                { model: aiModel }
            );
            removeLoading('Generating reading comprehension questions');

            if (multipleChoiceResult.error) {
                setMessage(multipleChoiceResult.error || 'Failed to generate multiple-choice questions.');
                return;
            }

            multipleChoiceTransformed = multipleChoiceResult.parsed;
            const multipleChoiceSection = buildMultipleChoiceSection(
                multipleChoiceTransformed,
                materialRichText,
                targetLanguage
            );
            if (!multipleChoiceSection.questions.length) {
                setMessage('AI did not return enough multiple-choice questions. Please try again.');
                return;
            }
            sections.push(multipleChoiceSection);
            sectionTitle = sectionTitle || multipleChoiceTransformed?.title || null;
        }

        const sectionLabels = [
            includeFillInBlank ? language.fillInBlankTitle : null,
            includeMultipleChoice ? language.multipleChoiceTitle : null,
        ].filter(Boolean);

        const { title: quizTitle, subtitle: quizSubtitle } = derivePaperTitleAndSubtitle({
            userTitle: title,
            userSubtitle: subtitle,
            materialTitle: materialMeta.title,
            materialSubtitle: materialMeta.subtitle,
            sectionTitle,
            sectionLabels,
            language,
            grade: paperMeta.grade,
            difficultyLabel: DIFFICULTY_LEVELS[difficulty]?.label,
        });

        const paper = normalizeLoadedPaper({
            ownerUserId: account.ownerUserId || account.id,
            author: {
                userId: account.id,
                fullName: account.fullName,
            },
            title: quizTitle,
            subtitle: quizSubtitle,
            subject: language.subject,
            school: paperMeta.school || null,
            grade: paperMeta.grade || null,
            taggings: paperMeta.taggings || [],
            languageCode: language.paperCode,
            fullScore: 100,
            timeLimitInMinutes: 30,
            richText: materialRichText,
            sections,
        });

        addLoading('Saving your Chinese quiz');
        try {
            const currentTimestamp = getFirestoreTimestamp();
            const docRef = await firestore.collection('test-papers').add(
                sanitizeForFirestore(Object.assign({}, paper, {
                    createdAt: currentTimestamp,
                    updatedAt: currentTimestamp,
                }))
            );
            removeLoading('Saving your Chinese quiz');
            goto(`/papers/${docRef.id}/edit?source=chinese-quiz`);
        } catch (error) {
            removeLoading('Saving your Chinese quiz');
            setMessage(error.message || 'Failed to save the quiz.');
        }
    };

    const selectedSummary = [
        includeFillInBlank ? `${fillInBlankCount || BASELINE_FILL_IN_BLANK_COUNT} fill-in-the-blank` : null,
        includeMultipleChoice ? `${multipleChoiceCount || BASELINE_MULTIPLE_CHOICE_COUNT} multiple-choice` : null,
    ].filter(Boolean).join(' + ') || 'no sections selected';

    return (
        <React.Fragment>
            {Boolean(loading.length) && <LoadingMask message={`${loading.join(' / ')}...`} />}
            <Container maxWidth="md">
                <Box py={3} textAlign="left">
                    <Typography variant="h5" className={classes.pageTitle}>
                        Create Chinese Quiz
                    </Typography>
                    <Typography variant="body2" className={classes.pageSubtitle}>
                        Paste a reading passage or ask AI to generate one, then choose which sections and how many
                        questions to create.
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                <Box>
                                    <Typography className={classes.sectionLabel}>Create for</Typography>
                                    <Typography className={classes.createForLabel}>
                                        {createForUser?.fullName
                                            ? `${createForUser.fullName}${paperMeta.grade ? ` · ${paperMeta.grade}` : ''}`
                                            : 'Select a user to pre-fill grade and school (optional)'}
                                    </Typography>
                                </Box>
                                <UserSelectionMenu
                                    account={account}
                                    user={user}
                                    onSelectUser={populateCreateForAttributes}
                                    buttonLabel="Select User"
                                    requirePassword={false}
                                    showDependentUsersOnly={false}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Quiz title (optional)"
                                variant="outlined"
                                fullWidth
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                placeholder="Auto-generated if left blank"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Quiz subtitle (optional)"
                                variant="outlined"
                                fullWidth
                                value={subtitle}
                                onChange={(event) => setSubtitle(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                placeholder="Auto-generated if left blank"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography className={classes.sectionLabel}>Target Language</Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={targetLanguage}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setTargetLanguage(newValue);
                                    }
                                }}
                                className={classes.toggleGroup}
                            >
                                {Object.entries(TARGET_LANGUAGES).map(([key, { label }]) => (
                                    <ToggleButton key={key} value={key} className={classes.toggleButton}>
                                        {label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography className={classes.sectionLabel}>Difficulty</Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={difficulty}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setDifficulty(newValue);
                                    }
                                }}
                                className={classes.toggleGroup}
                            >
                                {Object.entries(DIFFICULTY_LEVELS).map(([key, { label }]) => (
                                    <ToggleButton key={key} value={key} className={classes.toggleButton}>
                                        {label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                                Applies to AI-generated material and quiz questions.
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography className={classes.sectionLabel}>AI Model</Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={aiModel}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setAiModel(newValue);
                                    }
                                }}
                                className={classes.toggleGroup}
                            >
                                {Object.values(DEEPSEEK_MODELS).map(({ id, label }) => (
                                    <ToggleButton key={id} value={id} className={classes.toggleButton}>
                                        {label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                                {DEEPSEEK_MODELS[aiModel]?.description || 'Flash is faster and cheaper; Pro is higher quality.'}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography className={classes.sectionLabel}>AI prompt</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                Describe the topic or style of article you want AI to write.
                            </Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={3}
                                value={aiPrompt}
                                onChange={(event) => setAiPrompt(event.target.value)}
                                placeholder="Describe the reading material you want AI to generate..."
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label={targetLanguage === 'en' ? 'Target length (words)' : 'Target length (字)'}
                                variant="outlined"
                                fullWidth
                                type="number"
                                value={materialLength}
                                onChange={(event) => updateMaterialLength(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: 50, step: 50 }}
                                helperText="Question counts auto-adjust with length"
                            />
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Box className={classes.aiPromptActions} style={{ marginTop: 0, height: '100%', alignItems: 'flex-end', display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    startIcon={<Icon>auto_fix_high</Icon>}
                                    onClick={generateMaterial}
                                    disabled={Boolean(loading.length) || !aiPrompt.trim() || !materialLength}
                                >
                                    Generate Material
                                </Button>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography className={classes.sectionLabel}>Quiz sections</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                Choose which sections to generate and how many questions each should include.
                            </Typography>

                            <Box className={classes.sectionOptionRow}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={includeFillInBlank}
                                            onChange={(event) => setIncludeFillInBlank(event.target.checked)}
                                        />
                                    }
                                    label={<Typography className={classes.checkboxLabel}>Fill in the Blank</Typography>}
                                />
                                <TextField
                                    className={classes.questionCountField}
                                    label="Questions"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={fillInBlankCount}
                                    disabled={!includeFillInBlank}
                                    onChange={(event) => {
                                        const parsed = parseInt(event.target.value, 10);
                                        setFillInBlankCount(Number.isNaN(parsed) ? '' : parsed);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: 1, step: 1 }}
                                />
                            </Box>

                            <Box className={classes.sectionOptionRow}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={includeMultipleChoice}
                                            onChange={(event) => setIncludeMultipleChoice(event.target.checked)}
                                        />
                                    }
                                    label={<Typography className={classes.checkboxLabel}>Multiple Choice</Typography>}
                                />
                                <TextField
                                    className={classes.questionCountField}
                                    label="Questions"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={multipleChoiceCount}
                                    disabled={!includeMultipleChoice}
                                    onChange={(event) => {
                                        const parsed = parseInt(event.target.value, 10);
                                        setMultipleChoiceCount(Number.isNaN(parsed) ? '' : parsed);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: 1, step: 1 }}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography className={classes.sectionLabel}>Reading material</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                Paste an article here, or use AI above to generate one. Markdown formatting (e.g. **bold**) is converted for the editor.
                            </Typography>
                            <Box className={classes.materialBox}>
                                <RichTextEditor
                                    value={materialRichText}
                                    setValue={setMaterialRichText}
                                    showWordCount
                                    wordCountLanguage={targetLanguage === 'en' ? 'en' : 'zh'}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box className={classes.actionBar}>
                                <Typography variant="body2" color="textSecondary">
                                    {TARGET_LANGUAGES[targetLanguage]?.label} · {DIFFICULTY_LEVELS[difficulty]?.label} · {DEEPSEEK_MODELS[aiModel]?.label || 'Flash'} · {selectedSummary}
                                </Typography>
                                <Box>
                                    <Button color="default" onClick={() => goto('/papers')} style={{ marginRight: 8 }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        className={classes.generateButton}
                                        startIcon={<Icon>auto_awesome</Icon>}
                                        onClick={generateQuiz}
                                        disabled={
                                            Boolean(loading.length)
                                            || (!includeFillInBlank && !includeMultipleChoice)
                                        }
                                    >
                                        Generate Quiz
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </React.Fragment>
    );
}
